// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { leaderboardStrings } from './strings';

export type LeaderboardOptionKey = 'rename' | 'duplicate' | 'delete';

export interface LeaderboardOptionMenuProps {
  readonly visible: boolean;
  readonly onSelect: (key: LeaderboardOptionKey) => void;
  readonly onClose: () => void;
}

const ORDER: readonly LeaderboardOptionKey[] = ['rename', 'duplicate', 'delete'];

export const LeaderboardOptionMenu: React.FC<LeaderboardOptionMenuProps> = ({
  visible,
  onSelect,
  onClose,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={[styles.scrim, { backgroundColor: theme.colors.overlay.scrim }]}
        onPress={onClose}
      >
        <View
          style={[
            styles.menu,
            {
              backgroundColor: theme.colors.surface.primary,
              borderColor: theme.colors.border.secondary,
            },
          ]}
        >
          {ORDER.map((key) => (
            <Pressable
              key={key}
              onPress={() => {
                onSelect(key);
                onClose();
              }}
              accessibilityRole="button"
              accessibilityLabel={leaderboardStrings.optionMenu[key]}
              style={({ pressed }) => [
                styles.item,
                pressed && { backgroundColor: theme.colors.surface.tertiary },
              ]}
            >
              <Text
                style={[
                  styles.itemText,
                  {
                    color: key === 'delete' ? theme.colors.accent.rose : theme.colors.text.primary,
                  },
                ]}
              >
                {leaderboardStrings.optionMenu[key]}
              </Text>
            </Pressable>
          ))}
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  menu: {
    minWidth: 220,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  } satisfies ViewStyle,
  item: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  } satisfies ViewStyle,
  itemText: {
    fontSize: 14,
    fontWeight: '500',
  } satisfies TextStyle,
});
